import { Acl, Model } from '@contember/schema';
import { acceptFieldVisitor, isColumn, isInverseRelation, isOwningRelation, isRelation, PredicateDefinitionProcessor, } from '@contember/schema-utils';
import deepEqual from 'fast-deep-equal';
import { isAutoGeneratedColumnName, isRestrictedColumnName } from './utils';
import { excludeUndefinedDeep } from './utils/excludeUndefined';
import { emptyRole, updateAcl, updateAclEntities, updateAclEveryEntity, updateAclEveryPredicate, updateAclEveryRole, updateAclFieldPermissions, updateAclRole, } from './utils/schemaAclUpdateUtils';
import { updateEntity, updateEveryEntity, updateEveryField, updateField, updateModel, updateSchema, } from './utils/schemaUpdateUtils';
export const changeValue = (from, to) => (value) => value === from ? to : value;
export const isIt = (val, property, value) => {
    if (value !== undefined) {
        return val[property] === value;
    }
    return val[property] !== undefined;
};
export class SchemaBuilderError extends Error {
}
export class SchemaBuilder {
    constructor(schema, partsFactory, namingConventions, options) {
        Object.defineProperty(this, "schema", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: schema
        });
        Object.defineProperty(this, "partsFactory", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: partsFactory
        });
        Object.defineProperty(this, "namingConventions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: namingConventions
        });
        Object.defineProperty(this, "options", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: options
        });
    }
    addEntity(entity) {
        return this.updateModel(({ model }) => {
            if (entity.name in this.schema.model.entities) {
                throw new SchemaBuilderError(`Entity ${entity.name} already exists`);
            }
            const fullEntity = {
                ...this.partsFactory.createEntity(entity),
                fields: {
                    ...this.partsFactory.createEntity(entity).fields,
                    createdAt: {
                        name: 'createdAt',
                        type: Model.ColumnType.DateTime,
                        columnType: 'timestamptz',
                        nullable: false,
                        columnName: 'created_at',
                        default: 'now',
                    },
                },
            };
            return {
                ...model,
                entities: {
                    ...model.entities,
                    [fullEntity.name]: fullEntity,
                },
            };
        });
    }
    removeEntity(entityName) {
        if (!this.schema.model.entities[entityName]) {
            throw new SchemaBuilderError(`Entity ${entityName} does not exist.`);
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let builder = this;
        if (this.options.patchAcl !== false) {
            builder = builder.updateSchema(updateAcl(updateAclEveryRole(({ role }) => ({
                ...role,
                variables: Object.fromEntries(Object.entries(role.variables).filter(([, variable]) => variable.type !== Acl.VariableType.entity || variable.entityName !== entityName)),
            }), updateAclEntities(({ entities }) => {
                const { [entityName]: removed, ...other } = entities;
                return other;
            }), updateAclEveryEntity(updateAclEveryPredicate(({ predicate, entityName, schema }) => {
                const processor = new PredicateDefinitionProcessor(schema.model);
                const currentEntity = schema.model.entities[entityName];
                return processor.process(currentEntity, predicate, {
                    handleColumn: ctx => {
                        return ctx.entity.name === entityName ? undefined : ctx.value;
                    },
                    handleRelation: ctx => {
                        return ctx.entity.name === entityName ? undefined : ctx.value;
                    },
                });
            })))));
        }
        if (this.options.removeReferencingRelation !== false) {
            const fieldsToRemove = Object.values(builder.schema.model.entities).flatMap(entity => Object.values(entity.fields)
                .filter(field => isRelation(field) && field.target === entityName)
                .map((field) => [entity.name, field.name]));
            for (const field of fieldsToRemove) {
                builder = builder.removeField(field[0], field[1]);
            }
        }
        return builder.updateModel(({ model }) => {
            const { [entityName]: removed, ...entities } = model.entities;
            return {
                ...model,
                entities: { ...entities },
            };
        });
    }
    appendEntityData(currentEntity, newEntity) {
        return {
            ...currentEntity,
            ...newEntity,
            fields: {
                ...currentEntity.fields,
                ...newEntity.fields,
            },
        };
    }
    appendEntity(entityName, newEntity) {
        return this.updateModel(({ model }) => {
            const currentEntity = model.entities[entityName] ?? {};
            const entity = this.appendEntityData(currentEntity, newEntity);
            return {
                ...model,
                entities: {
                    ...model.entities,
                    [entity.name]: entity,
                },
            };
        });
    }
    appendEntities(entities) {
        return this.updateModel(({ model }) => {
            const newEntities = { ...model.entities };
            for (const entityName in entities) {
                const currentEntity = model.entities[entityName] ?? {};
                const newEntity = entities[entityName];
                newEntities[entityName] = this.appendEntityData(currentEntity, newEntity);
            }
            return {
                ...model,
                entities: newEntities,
            };
        });
    }
    updateEntityName(entityName, newEntityName, { renameTable } = {}) {
        let builder = this.updateModel(updateEveryEntity(updateEveryField(({ field }) => {
            if (isIt(field, 'target') && field.target === entityName) {
                return { ...field, target: newEntityName };
            }
            return field;
        }))).updateModel(({ model }) => {
            const { [entityName]: renamed, ...entities } = model.entities;
            const newEntities = {
                ...entities,
                [newEntityName]: {
                    ...renamed,
                    ...(renameTable ? { tableName: this.namingConventions.getTableName(newEntityName) } : {}),
                    name: newEntityName,
                },
            };
            return {
                ...model,
                entities: newEntities,
            };
        });
        if (this.options.patchAcl !== false) {
            builder = builder.updateSchema(updateAcl(updateAclEveryRole(({ role }) => ({
                ...role,
                variables: Object.fromEntries(Object.entries(role.variables).map(([key, variable]) => {
                    if (variable.type === Acl.VariableType.entity) {
                        return [
                            key,
                            {
                                ...variable,
                                entityName: changeValue(entityName, newEntityName)(variable.entityName),
                            },
                        ];
                    }
                    return [key, variable];
                })),
            }), updateAclEntities(({ entities }) => {
                if (!entities[entityName]) {
                    return entities;
                }
                const { [entityName]: renamed, ...other } = entities;
                return {
                    [newEntityName]: renamed,
                    ...other,
                };
            }))));
        }
        return builder;
    }
    createUnique(entityName, unique) {
        return this.updateEntity(entityName, ({ entity }) => {
            return {
                ...entity,
                unique: [...entity.unique, unique],
            };
        });
    }
    removeUnique(entityName, unique) {
        return this.updateEntity(entityName, ({ entity }) => {
            const newUnique = entity.unique.filter(it => !deepEqual(it.fields, unique.fields));
            return {
                ...entity,
                unique: newUnique,
            };
        });
    }
    createIndex(entityName, index) {
        return this.updateEntity(entityName, ({ entity }) => {
            return {
                ...entity,
                indexes: [...entity.indexes, index],
            };
        });
    }
    removeIndex(entityName, index) {
        return this.updateEntity(entityName, ({ entity }) => {
            const indexes = entity.indexes.filter(it => !deepEqual(it.fields, index.fields));
            return {
                ...entity,
                indexes,
            };
        });
    }
    addColumn(entityName, column) {
        if (isRestrictedColumnName(column.name)) {
            if (isAutoGeneratedColumnName(column.name)) {
                throw new SchemaBuilderError(`Column ${column.name} is available on every entity and cannot be added manually.`);
            }
            throw new SchemaBuilderError(`Column ${column.name} is reserved.`);
        }
        return this.addField(entityName, this.partsFactory.createColumn(column));
    }
    addRelation(entityName, relation) {
        const field = this.partsFactory.createRelation(entityName, relation);
        const targetEntity = this.schema.model.entities[field.target];
        if (!targetEntity) {
            throw new SchemaBuilderError(`Target entity ${field.target} does not exist`);
        }
        return this.addField(entityName, field);
    }
    addField(entityName, field) {
        return this.updateEntity(entityName, ({ entity }) => {
            if (field.name in entity.fields) {
                throw new SchemaBuilderError(`Field ${field.name} already exists in entity ${entityName}.`);
            }
            return {
                ...entity,
                fields: {
                    ...entity.fields,
                    [field.name]: field,
                },
            };
        });
    }
    removeField(entityName, fieldName) {
        let builder = this;
        const entity = this.schema.model.entities[entityName];
        if (!entity) {
            throw new SchemaBuilderError(`Entity ${entityName} does not exist`);
        }
        const field = entity.fields[fieldName];
        if (!field) {
            throw new SchemaBuilderError(`Field ${fieldName} does not exist in entity ${entityName}`);
        }
        if (this.options.patchAcl !== false) {
            builder = builder.updateSchema(updateAcl(updateAclEveryRole(updateAclEveryEntity(updateAclFieldPermissions((permissions, entityName) => {
                if (entityName !== entity.name) {
                    return permissions;
                }
                const { [fieldName]: field, ...other } = permissions ?? {};
                return {
                    ...other,
                };
            }), updateAclEveryPredicate(({ predicate, entityName, schema }) => {
                const processor = new PredicateDefinitionProcessor(schema.model);
                return processor.process(schema.model.entities[entityName], predicate, {
                    handleColumn: ctx => (ctx.entity.name === entity.name && ctx.column.name === field.name ? undefined : ctx.value),
                    handleRelation: ctx => (ctx.entity.name === entity.name && ctx.relation.name === field.name ? undefined : ctx.value),
                });
            })))));
        }
        if (isRelation(field) && isOwningRelation(field) && field.inversedBy && this.options.removeRelationInverseSide !== false) {
            builder = builder.removeField(field.target, field.inversedBy);
        }
        builder = builder.updateEntity(entity.name, ({ entity }) => {
            const { [field.name]: removed, ...fields } = entity.fields;
            const indexes = entity.indexes.filter(index => !index.fields.includes(field.name));
            const unique = entity.unique.filter(index => !index.fields.includes(field.name));
            return {
                ...entity,
                fields,
                indexes,
                unique,
            };
        });
        if (isRelation(field) && isInverseRelation(field)) {
            builder = builder.updateEntity(field.target, updateField(field.ownedBy, ({ field: { inversedBy, ...field } }) => field));
        }
        return builder;
    }
    updateFieldName(entityName, fieldName, newFieldName) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let builder = this;
        if (this.options.patchAcl !== false) {
            builder = builder.updateSchema(updateAcl(updateAclEveryRole(updateAclEveryEntity(updateAclFieldPermissions((permissions, fieldEntityName) => {
                if (entityName !== fieldEntityName) {
                    return permissions;
                }
                if (!permissions[fieldName]) {
                    return permissions;
                }
                const { [fieldName]: field, ...other } = permissions;
                return {
                    [newFieldName]: field,
                    ...other,
                };
            }), updateAclEveryPredicate(({ predicate, entityName, schema }) => {
                const processor = new PredicateDefinitionProcessor(schema.model);
                const currentEntity = schema.model.entities[entityName];
                return processor.process(currentEntity, predicate, {
                    handleColumn: ctx => (ctx.entity.name === entityName && ctx.column.name === fieldName ? [newFieldName, ctx.value] : ctx.value),
                    handleRelation: ctx => (ctx.entity.name === entityName && ctx.relation.name === fieldName ? [newFieldName, ctx.value] : ctx.value),
                });
            })))));
        }
        if (this.options.updateConstraintFields !== false) {
            builder = builder.updateEntity(entityName, ({ entity }) => {
                return {
                    ...entity,
                    unique: entity.unique?.map(unique => ({
                        ...unique,
                        fields: unique.fields?.map(changeValue(fieldName, newFieldName)),
                    })),
                };
            });
        }
        if (this.options.updateIndexesFields !== false) {
            builder = builder.updateEntity(entityName, ({ entity }) => {
                return {
                    ...entity,
                    indexes: entity.indexes?.map(unique => ({
                        ...unique,
                        fields: unique.fields?.map(changeValue(fieldName, newFieldName)),
                    })),
                };
            });
        }
        builder = builder
            .updateModel(updateEveryEntity(updateEveryField(({ field, entity }) => {
            const isUpdatedRelation = (entity, relation) => {
                return entity.name === entityName && relation && relation.name === fieldName;
            };
            return acceptFieldVisitor(builder.schema.model, entity, field, {
                visitColumn: ({ column }) => column,
                visitManyHasOne: ({ targetEntity, relation, targetRelation }) => {
                    return isUpdatedRelation(targetEntity, targetRelation) ? { ...relation, inversedBy: newFieldName } : relation;
                },
                visitOneHasMany: ({ relation, targetEntity, targetRelation }) => {
                    return isUpdatedRelation(targetEntity, targetRelation) ? { ...relation, ownedBy: newFieldName } : relation;
                },
                visitOneHasOneOwning: ({ relation, targetEntity, targetRelation }) => {
                    return isUpdatedRelation(targetEntity, targetRelation) ? { ...relation, inversedBy: newFieldName } : relation;
                },
                visitOneHasOneInverse: ({ relation, targetEntity, targetRelation }) => {
                    return isUpdatedRelation(targetEntity, targetRelation) ? { ...relation, ownedBy: newFieldName } : relation;
                },
                visitManyHasManyOwning: ({ relation, targetEntity, targetRelation }) => {
                    return isUpdatedRelation(targetEntity, targetRelation) ? { ...relation, inversedBy: newFieldName } : relation;
                },
                visitManyHasManyInverse: ({ relation, targetEntity, targetRelation }) => {
                    return isUpdatedRelation(targetEntity, targetRelation) ? { ...relation, ownedBy: newFieldName } : relation;
                },
            });
        })))
            .updateEntity(entityName, ({ entity }) => {
            const { [fieldName]: updated, ...fields } = entity.fields;
            return {
                ...entity,
                fields: {
                    ...fields,
                    [newFieldName]: { ...updated, name: newFieldName },
                },
            };
        });
        return builder;
    }
    updateColumnType(entityName, fieldName, type) {
        return this.updateField(entityName, fieldName, isFieldType(['column']), ({ field }) => ({
            ...this.partsFactory.createColumn({
                ...field,
                type: type.type,
                columnType: this.partsFactory.createColumnType(type),
                nullable: type.nullable ?? field.nullable,
            }),
        }));
    }
    updateColumnDefault(entityName, fieldName, defaultValue) {
        return this.updateField(entityName, fieldName, isFieldType(['column']), ({ field }) => ({
            ...field,
            default: defaultValue,
        }));
    }
    updateRelationOnDelete(entityName, fieldName, onDelete) {
        const field = this.getField(entityName, fieldName);
        let entityNameTemp = entityName;
        let fieldNameTemp = fieldName;
        if (field?.type === Model.RelationType.OneHasMany) {
            entityNameTemp = field.target;
            fieldNameTemp = field.ownedBy;
        }
        return this.updateField(entityNameTemp, fieldNameTemp, isFieldType(['oneHasOneOwning', 'manyHasOne']), ({ field }) => ({
            ...field,
            joiningColumn: { ...field.joiningColumn, onDelete },
        }));
    }
    updateRelationOrderBy(entityName, fieldName, orderBy) {
        return this.updateField(entityName, fieldName, isFieldType(['oneHasMany', 'manyHasManyOwning', 'manyHasManyInverse']), ({ field }) => ({
            ...field,
            orderBy,
        }));
    }
    updateRelationOrphanRemoval(entityName, fieldName, newValue) {
        return this.updateField(entityName, fieldName, isFieldType(['oneHasOneOwning']), ({ field: { orphanRemoval, ...field } }) => ({
            ...field,
            ...(newValue
                ? {
                    orphanRemoval: true,
                }
                : {}),
        }));
    }
    updateRelationNullable(entityName, fieldName, nullable) {
        return this.updateField(entityName, fieldName, isFieldType(['oneHasOneOwning', 'oneHasOneInverse', 'manyHasOne']), ({ field }) => ({
            ...field,
            nullable,
        }));
    }
    updateJunctionEventLog(entityName, fieldName, enabled) {
        return this.updateField(entityName, fieldName, isFieldType(['manyHasManyOwning']), ({ field: { joiningTable: { eventLog, ...joiningTable }, ...field }, }) => {
            return {
                ...field,
                joiningTable: {
                    ...joiningTable,
                    eventLog: {
                        enabled,
                    },
                },
            };
        });
    }
    addEnum(_enum) {
        if (_enum.enumName in this.schema.model.enums) {
            throw new SchemaBuilderError(`Enum ${_enum.enumName} already exists`);
        }
        return this.updateModel(({ model }) => ({
            ...model,
            enums: {
                ...model.enums,
                [_enum.enumName]: _enum.values,
            },
        }));
    }
    removeEnum(enumName) {
        if (!(enumName in this.schema.model.enums)) {
            throw new SchemaBuilderError(`Enum ${enumName} does not exist`);
        }
        let builder = this;
        const enumEntities = Object.entries(this.schema.model.entities).filter(([, entity]) => {
            return Object.values(entity.fields).some(field => isColumn(field) && field.columnType === enumName);
        });
        builder = builder.updateSchema(updateAcl(updateAclEveryRole(updateAclEveryEntity(updateAclFieldPermissions((permissions, entityName) => {
            const enumEntity = enumEntities.find(([name]) => name === entityName);
            if (!enumEntity) {
                return permissions;
            }
            const [name, entity] = enumEntity;
            const fieldName = Object.values(entity.fields).find(field => isColumn(field) && field.columnType === enumName)?.name;
            if (!fieldName) {
                return permissions;
            }
            const { [fieldName]: field, ...other } = permissions ?? {};
            return {
                ...other,
            };
        }), updateAclEveryPredicate(({ predicate, entityName, schema }) => {
            const enumEntity = enumEntities.find(([name]) => name === entityName);
            if (!enumEntity) {
                return predicate;
            }
            const [name, entity] = enumEntity;
            const fieldName = Object.values(entity.fields).find(field => isColumn(field) && field.columnType === enumName)?.name;
            if (!fieldName) {
                return predicate;
            }
            const processor = new PredicateDefinitionProcessor(schema.model);
            return processor.process(schema.model.entities[entityName], predicate, {
                handleColumn: ctx => (ctx.entity.name === enumEntity[0] && ctx.column.name === fieldName ? undefined : ctx.value),
                handleRelation: ctx => (ctx.entity.name === enumEntity[0] && ctx.relation.name === fieldName ? undefined : ctx.value),
            });
        })))));
        builder = builder.updateModel(({ model }) => {
            const { [enumName]: removedEnum, ...enums } = model.enums;
            return {
                ...model,
                entities: {
                    ...model.entities,
                    ...Object.fromEntries(Object.entries(model.entities).map(([entityName, entity]) => {
                        const fields = Object.fromEntries(Object.entries(entity.fields)
                            .map(([fieldName, field]) => {
                            if (isColumn(field) && field.columnType === enumName) {
                                return [fieldName, null];
                            }
                            return [fieldName, field];
                        })
                            .filter(([, field]) => field !== null));
                        return [entityName, { ...entity, fields }];
                    })),
                },
                enums,
            };
        });
        return builder;
    }
    mergeEnums(merge) {
        return this.updateModel(({ model }) => {
            const enums = { ...model.enums };
            for (const [enumName, values] of Object.entries(merge)) {
                if (enumName in enums) {
                    enums[enumName] = [...new Set([...enums[enumName], ...values])];
                }
                else {
                    enums[enumName] = values;
                }
            }
            return {
                ...model,
                enums,
            };
        });
    }
    updateEnumValues(enumName, updater) {
        const enumValues = this.schema.model.enums[enumName];
        if (!enumValues) {
            throw new SchemaBuilderError(`Enum ${enumName} does not exist`);
        }
        const newValues = updater(enumValues);
        if (newValues.length === 0) {
            return this.removeEnum(enumName);
        }
        return this.updateModel(({ model }) => ({
            ...model,
            enums: {
                ...model.enums,
                [enumName]: updater(model.enums[enumName]),
            },
        }));
    }
    updateSchema(schemaUpdater) {
        return this.withSchema(updateSchema(schemaUpdater)({ schema: this.schema }));
    }
    updateModel(modelUpdater) {
        return this.withSchema(updateModel(modelUpdater)({ schema: this.schema }));
    }
    createAclRole(role) {
        if (this.schema.acl.roles[role]) {
            return this.updateSchema(({ schema }) => schema);
        }
        return this.updateSchema(updateAcl(({ acl }) => {
            return {
                ...acl,
                roles: {
                    ...acl.roles,
                    [role]: emptyRole,
                },
            };
        }));
    }
    removeAclRole(role) {
        return this.updateSchema(updateAcl(({ acl }) => {
            const { [role]: removed, ...roles } = acl.roles;
            return {
                ...acl,
                roles,
            };
        }));
    }
    renameAclRole(role, newRole) {
        return this.updateSchema(updateAcl(({ acl }) => {
            const { [role]: removed, ...roles } = acl.roles;
            return {
                ...acl,
                roles: {
                    ...roles,
                    [newRole]: acl.roles[role],
                },
            };
        }));
    }
    updatePermissionsEntity(roleName, entityName, permissions) {
        const mergeOperations = (existingOps = {}, newOps = {}) => {
            if (Object.keys(newOps).length === 0 && newOps.constructor === Object) {
                return existingOps;
            }
            return excludeUndefinedDeep({
                ...existingOps,
                ...newOps,
                read: newOps.read ? { ...existingOps.read, ...newOps.read } : existingOps.read,
                create: newOps.create ? { ...existingOps.create, ...newOps.create } : existingOps.create,
                update: newOps.update ? { ...existingOps.update, ...newOps.update } : existingOps.update,
                delete: newOps.delete ?? existingOps.delete,
            });
        };
        return this.updateSchema(updateAcl(updateAclRole(({ role, roleName: currentRoleName }) => {
            if (currentRoleName !== roleName)
                return role;
            const updatedOperations = mergeOperations(role.entities[entityName]?.operations, permissions.operations);
            const updatedEntities = {
                ...role.entities,
                [entityName]: {
                    operations: updatedOperations,
                    predicates: {
                        ...role.entities[entityName]?.predicates,
                        ...permissions.predicates,
                    },
                },
            };
            if (Object.keys(updatedOperations).length === 0) {
                delete updatedEntities[entityName];
            }
            return { ...role, entities: updatedEntities };
        })));
    }
    updateEntity(entityName, entityUpdater) {
        const entity = this.schema.model.entities[entityName];
        if (!entity) {
            throw new SchemaBuilderError(`Entity ${entityName} does not exist`);
        }
        return this.updateModel(updateEntity(entityName, entityUpdater));
    }
    updateField(entityName, fieldName, fieldAssertion, fieldUpdater) {
        return this.updateEntity(entityName, args => {
            if (!(fieldName in args.entity.fields)) {
                throw new SchemaBuilderError(`Field ${fieldName} does not exist in entity ${entityName}`);
            }
            const field = args.entity.fields[fieldName];
            if (fieldAssertion && !fieldAssertion(args.model, args.entity, field)) {
                throw new SchemaBuilderError(`Field ${fieldName} of type ${field.type} in entity ${entityName} is not of expected type`);
            }
            return updateField(fieldName, fieldUpdater)(args);
        });
    }
    createView(entityName, view) {
        return this.updateEntity(entityName, ({ entity }) => {
            return {
                ...entity,
                view,
            };
        });
    }
    withSchema(schema) {
        return new SchemaBuilder(schema, this.partsFactory, this.namingConventions, this.options);
    }
    getEntity(entityName) {
        const entity = this.schema.model.entities[entityName];
        if (!entity) {
            throw new SchemaBuilderError(`Entity ${entityName} does not exist`);
        }
        return entity;
    }
    getField(entityName, fieldName) {
        const entity = this.getEntity(entityName);
        const field = entity?.fields[fieldName];
        if (!field) {
            throw new SchemaBuilderError(`Field ${fieldName} does not exist in entity ${entityName}`);
        }
        return field;
    }
}
export const isFieldType = (types) => {
    return (model, entity, field) => {
        return acceptFieldVisitor(model, entity, field, {
            visitColumn: () => types.includes('column'),
            visitManyHasOne: () => types.includes('manyHasOne'),
            visitOneHasMany: () => types.includes('oneHasMany'),
            visitOneHasOneOwning: () => types.includes('oneHasOneOwning'),
            visitOneHasOneInverse: () => types.includes('oneHasOneInverse'),
            visitManyHasManyOwning: () => types.includes('manyHasManyOwning'),
            visitManyHasManyInverse: () => types.includes('manyHasManyInverse'),
        });
    };
};
//# sourceMappingURL=SchemaBuilder.js.map